/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {Events} from '@exc/shared';
import type {Metrics, MetricsApi} from '@adobe/exc-app/metrics';
import type {RuntimeMessenger} from './models/runtimeModels';

const IGNORE_ELEMENTS = new Set(['div', 'label', 'path', 'span']);
let quietModeEnabled = false;
let toastTrackingEnabled = false;

interface ElementDetails {
  className: string;
  name: string;
  type: string | null;
}

interface ToastData {
  childElements: ElementDetails[];
  content: string;
  html: string;
  type: string;
}

/**
 * Extract necessary data from the toast element that has been received.
 * @param element - Toast element to extract from.
 * @returns - The extracted toast data.
 */
const extractToastData = (element: HTMLElement): ToastData => {
  const body = element.querySelector('.spectrum-Toast-body') as HTMLElement;
  const content = body.querySelector('.spectrum-Toast-content') as HTMLElement;

  return {
    childElements: getAllChildNodes(body),
    content: content?.textContent?.trim() || '',
    html: body.innerHTML.trim(),
    type: element.className.match(/spectrum-Toast--([^ |$]*)/)?.[1] || 'info'
  };
};

/**
 * Recursively generate a list of all child nodes of an element.
 * @param element - Element to get child ElementDetails for.
 * @returns - Array of child ElementDetails.
 */
const getAllChildNodes = (element: Element | HTMLElement): ElementDetails[] => {
  let details: ElementDetails[] = [];
  for (const child of element.children || []) {
    const elementDetails = getElementDetails(child);
    details = details.concat(
      IGNORE_ELEMENTS.has(elementDetails.name) ? [] : [elementDetails],
      child.children.length === 0 ? [] : getAllChildNodes(child)
    );
  }
  return details;
};

/**
 * Get relevant element details.
 * @param element - Element to get details for.
 * @returns - Element details.
 */
const getElementDetails = (element: Element): ElementDetails => ({
  className: element.className,
  name: element.nodeName.toLowerCase(),
  type: element.getAttribute('type')
});

/**
 * Process the toast node, logging the content and type to metrics / console.
 * @param node - The toast node to process.
 * @param messenger - Messenger instance.
 * @param metrics - Metrics object for logging.
 */
export const processToastNode = (node: Node, messenger: RuntimeMessenger, metrics: Metrics): void => {
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return;
  }
  const element = node as HTMLElement;
  // There have been some instances where the class list is an object. This
  // isn't supported by this function, so we can ignore it.
  if (typeof element?.className !== 'string') {
    return;
  }
  if (!element?.className?.split(' ')?.includes('spectrum-Toast')) {
    return;
  }
  const data = extractToastData(element);
  metrics.info('Toast Received', data);
  if (quietModeEnabled) {
    metrics.warn('Toast Hijacked', data);
    messenger.send(Events.TOAST_SUPRESSED, data.content);
  }
};

/**
 * Quiet mode enablement function. Ensures that it's only enabled once. Adds CSS
 * to the DOM to hide toasts. Adds a mutation observer to find out when toasts
 * are added and to warn on their contents.
 */
export const enable = (messenger: RuntimeMessenger, metricsApi: MetricsApi, window: Window & typeof globalThis, toastQuietModeEnabled: boolean): void => {
  const {document, MutationObserver} = window;
  toastQuietModeEnabled && enableQuietMode(document);
  quietModeEnabled = toastQuietModeEnabled;

  // If toast tracking is enabled already, don't process past here again as it
  // sets up the observer and there needs to be only 1.
  if (toastTrackingEnabled) {
    return;
  }
  toastTrackingEnabled = true;
  const metrics = metricsApi.create('exc.module-runtime.toast');

  // Set up the observer to listen for changes on the toast container for any
  // new child elements (toasts).
  const observer = new MutationObserver((mutationsList: MutationRecord[]) =>
    mutationsList.forEach((mutation: MutationRecord) =>
      mutation.addedNodes.forEach((node: Node) => processToastNode(node, messenger, metrics))));
  observer.observe(document.body, {childList: true, subtree: true});
};

/**
 * Enable quiet mode, once, which hides toasts from view via CSS.
 * @param document - The document to add the CSS to.
 */
const enableQuietMode = (document: Document): void => {
  if (document.querySelector('head #quiet-mode')) {
    return;
  }

  // Create a style element with the CSS to hide the toast container element
  // from view and add it to the head of the document.
  const style = document.createElement('style');
  style.id = 'quiet-mode';
  style.innerHTML = `
    .react-spectrum-ToastContainer .spectrum-Toast--error,
    .react-spectrum-ToastContainer .spectrum-Toast--warning {
      display: none !important;
    }
  `;
  document.head.append(style);
};
